import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import constants from 'constants/constants';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColorTextByValue } from 'components/common/ColorTextByValue/ColorTextByValue';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';
import { portfolioConfig } from 'components/Dashboards/Portfolio/portfolioConfig';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import {
    IComponentProps,
    ILocalFilters,
    ISetSubTitleForExport,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Switch } from 'components/DesignSystem/Switch';
import { useTimelineAndDensityLogic } from 'components/widgets/timelineAndDensity/TimelineAndDensity.logic';
import { useTimelineAndDensityProvider } from 'components/widgets/timelineAndDensity/TimelineAndDensity.provider';
import { HeatmapWrapper } from 'components/widgets/timelineAndDensity/components/heatmapWrapper/heatmapWrapper';
import classes from 'components/widgets/timelineAndDensity/timelineAndDensity.module.scss';

export interface Props {
    widgetId?: string;
    externalComponents?: React.ReactNode;
    editNarrative?: React.ReactNode;
    setSubTitleForExport?: ISetSubTitleForExport;
    globalFilterData: any;
    localFilters?: ILocalFilters;
    reportWidgetRef?: IComponentProps['reportWidgetRef'];
    widgetViewMode?: IComponentProps['widgetViewMode'];
    widgetSelectedProjectAndContract?: IComponentProps['widgetSelectedProjectAndContract'];
    title: string;
    setIsPayloadReady?: (newValue: boolean) => void;
}

const { id, componentKey, endpoint } = widgetsConfigurations.timelineAndDensity;

export const TimelineAndDensity = ({
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    globalFilterData,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = 'Portfolio Timeline & Density',
    setIsPayloadReady,
}: Props) => {
    const { ui, api } = useTimelineAndDensityProvider({
        widgetId,
        localFilters,
        globalFilterData,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId: undefined,
        contractId: undefined,
        latestVersionId: undefined,
        title,
        setSubTitleForExport,
        customSubTitle: `${portfolioConfig.portfolioOverview.title}, ${prepareVersionSelectTitle()}`,
        ui,
        reportWidgetRef,
        apiPayload: api.getTimelineAndDensity.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useTimelineAndDensityLogic({ widgetViewMode, setIsPayloadReady, api });

    const data = api.getTimelineAndDensity.instance.data;
    const isLoading = api.getTimelineAndDensity.instance.isLoading;

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    <div key={'density'}>
                        <Switch
                            onChange={logic.handleShowDensity}
                            checked={logic.isShowDensity}
                            label={'Show Density'}
                        />
                    </div>,
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        widgetId={widgetId}
                        route={route}
                        projectId={null}
                        contractId={null}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={constants.NO_PROJECT_CONTRACT_ID}
                        contractId={constants.NO_PROJECT_CONTRACT_ID}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.getTimelineAndDensity.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon key={'info'} tooltip={data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        key={'pinboardPills'}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
            >
                {data && data.heatMapPortfolioProjectResponse.length === 0 ? (
                    <NoData />
                ) : (
                    <div className={classes.timelineAndDensityContainer}>
                        {isLoading && <OverlayWithSpinner />}
                        <div className={classes.titleContainer}>
                            {data?.heatMapPortfolioProjectResponse.map((item, index) => {
                                const first = index === 0;

                                return (
                                    <div className={classes.title} style={{ height: first ? 170 : 120 }} key={item.id}>
                                        {item.versionHashCode ? (
                                            <>
                                                <Link
                                                    data-testid={'link-lable'}
                                                    to={AppRoutes.buildPath(
                                                        AppRoutes.DASHBOARD_RISK_APPS_PATH,
                                                        riskConfig.criticalityAnalysis.link,
                                                        item.versionHashCode,
                                                    )}
                                                    className={classes.linkLabel}
                                                >
                                                    {item.label}
                                                </Link>
                                                <div>
                                                    Submitted Completion:{' '}
                                                    <span className={classes.submittedCompletionValue}>
                                                        {item.completionPercent}%
                                                    </span>
                                                </div>
                                                <div>
                                                    Schedule Performance (SPI):{' '}
                                                    {item.preferredSpiMethod === 'CriticalityWeighted' ? (
                                                        <ColorTextByValue value={item.spiCriticality} />
                                                    ) : (
                                                        <ColorTextByValue value={item.spiMilestone} />
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <div className={classes.label}>{item.label}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={classes.mapContainer} ref={logic.mapContainerRef}>
                            <div className={classes.todayLine} ref={logic.todayLineRef}>
                                <div className={classes.dateBox}>
                                    {moment(logic.todayLineDate).format(constants.formats.date.monthAndYear)}
                                </div>
                            </div>
                            {data &&
                                data.heatMapPortfolioProjectResponse.map((item, index) => {
                                    const first = index === 0;
                                    return (
                                        <div key={item.id} className={classes.mapWrapper}>
                                            <HeatmapWrapper
                                                mainData={data.heatMapPortfolioProjectResponse[0].data}
                                                data={data.heatMapPortfolioProjectResponse[index].data}
                                                isFirst={first}
                                                height={first ? 147 : 72}
                                                callback={logic.setTodayLine}
                                                todayLineDate={logic.todayLineDate}
                                                isShowDensity={logic.isShowDensity}
                                                isBaseline={false}
                                            />

                                            {data.heatMapPortfolioProjectResponse[index].baselineData.length !== 0 && (
                                                <div className={classes.baseline}>
                                                    <HeatmapWrapper
                                                        mainData={data.heatMapPortfolioProjectResponse[0].data}
                                                        data={data.heatMapPortfolioProjectResponse[index].baselineData}
                                                        height={10}
                                                        isShowDensity={logic.isShowDensity}
                                                        isBaseline={true}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
